/**
 * Skin modifier — pure typed-array core (Three.js-free).
 *
 * Consumes wire edges from the input ModifierGeometry and generates tube
 * surfaces around them, using RMF-framed circular profiles.
 *
 * Output is the original face geometry (if any) concatenated with the
 * generated skin triangles. Wire edges and edgeFlags are set to null on
 * the output (consumed by this modifier).
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { provenanceFromSourceMap } from "../../modeling/provenance.ts";
import { generateSkinMesh } from "../../modeling/skinGeometry.ts";
import { asSharedTriangleIndices, EDGE_WIRE } from "../../modeling/types.ts";
import { projectVertexGroupThroughProvenance } from "./helpers.ts";
import type { GeometryGroup, ModifierGeometry } from "./modifierTypes.ts";

export function skinCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "skin" }>,
): ModifierGeometry {
  // No wire edges → pass through unchanged
  if (!geo.edges || !geo.edgeFlags || geo.edges.length === 0) {
    return geo;
  }

  // When a vertex group is active, only wire edges whose both endpoints
  // belong to the group generate tubes.
  const allowed =
    projectVertexGroupThroughProvenance(
      mod.vertexGroupIndices,
      geo.provenance,
    ) ?? undefined;

  // Filter to wire-only edges, additionally restricted to group members when active.
  const edgeCount = geo.edges.length / 2;
  const wireEdges: number[] = [];
  for (let i = 0; i < edgeCount; i++) {
    const e0 = geo.edges[i * 2],
      e1 = geo.edges[i * 2 + 1];
    if (
      geo.edgeFlags[i] & EDGE_WIRE &&
      (!allowed || (allowed.has(e0) && allowed.has(e1)))
    ) {
      wireEdges.push(e0, e1);
    }
  }

  if (wireEdges.length === 0) return geo;

  const vertCount = geo.positions.length / 3;

  // Per-vertex radii: multiply each vertex's scale factor by mod.depth.
  // Fall back to a uniform mod.depth array when no per-vertex data is set.
  let radii: Float32Array;
  if (geo.skinRadii) {
    radii = new Float32Array(vertCount);
    for (let i = 0; i < vertCount; i++) {
      radii[i] = Math.max(0, geo.skinRadii[i]) * mod.depth;
    }
  } else {
    radii = new Float32Array(vertCount).fill(mod.depth);
  }

  const skin = generateSkinMesh({
    positions: geo.positions,
    edges: new Uint32Array(wireEdges),
    radii,
    subdivisions: mod.subdivisions,
    fillCaps: mod.fillCaps,
    capStyle: mod.capStyle,
    preserveVolume: mod.preserveVolume,
    style: mod.style,
    strandCount: mod.strandCount,
    twistRate: mod.twistRate,
    bundleJitter: mod.bundleJitter,
    ribFrequency: mod.ribFrequency,
    ribDepth: mod.ribDepth,
    linkLength: mod.linkLength,
    linkAspect: mod.linkAspect,
    wireThickness: mod.wireThickness,
    linkOverlap: mod.linkOverlap,
    // UI stores twistPerLink in degrees so users think in familiar units;
    // the WASM generator wants radians.
    twistPerLink: ((mod.twistPerLink ?? 90) * Math.PI) / 180,
    linkSegments: mod.linkSegments,
  });

  const skinVertCount = skin.positions.length / 3;
  if (skinVertCount === 0) return geo;

  // --- Concatenate existing face geometry + skin geometry ---
  const combinedPos = new Float32Array(
    geo.positions.length + skin.positions.length,
  );
  combinedPos.set(geo.positions, 0);
  combinedPos.set(skin.positions, geo.positions.length);

  // Offset skin indices by existing vertex count
  const combinedIdx = new Uint32Array(geo.indices.length + skin.indices.length);
  combinedIdx.set(geo.indices, 0);
  for (let i = 0; i < skin.indices.length; i++) {
    combinedIdx[geo.indices.length + i] = skin.indices[i] + vertCount;
  }

  // Normals: concat existing (or zero-pad) with skin normals
  let combinedNorm: Float32Array | null = null;
  if (geo.normals) {
    combinedNorm = new Float32Array(geo.normals.length + skin.normals.length);
    combinedNorm.set(geo.normals, 0);
    combinedNorm.set(skin.normals, geo.normals.length);
  } else if (skin.normals.length > 0) {
    // Pad existing-vert section with zeros; skin verts have real normals
    combinedNorm = new Float32Array(vertCount * 3 + skin.normals.length);
    combinedNorm.set(skin.normals, vertCount * 3);
  }

  // UVs — zero-extend for new skin verts
  let combinedUVs: Float32Array | null = null;
  if (geo.uvs) {
    combinedUVs = new Float32Array(geo.uvs.length + skinVertCount * 2);
    combinedUVs.set(geo.uvs, 0);
    // skin UV entries remain 0
  }

  // Morph arrays — zero-extend
  const combinedMorphs = geo.morphArrays.map((deltas) => {
    const out = new Float32Array(deltas.length + skinVertCount * 3);
    out.set(deltas, 0);
    return out;
  });

  // Sculpt displacement — zero-extend
  let combinedSculpt: Float32Array | null = null;
  if (geo.sculptDisplacement) {
    combinedSculpt = new Float32Array(
      geo.sculptDisplacement.length + skinVertCount * 3,
    );
    combinedSculpt.set(geo.sculptDisplacement, 0);
  }

  // Armature skin weights — zero-extend
  let combinedSkinIdx: Float32Array | null = null;
  let combinedSkinWgt: Float32Array | null = null;
  if (geo.skinIndex && geo.skinWeight) {
    combinedSkinIdx = new Float32Array(
      geo.skinIndex.length + skinVertCount * 4,
    );
    combinedSkinIdx.set(geo.skinIndex, 0);
    combinedSkinWgt = new Float32Array(
      geo.skinWeight.length + skinVertCount * 4,
    );
    combinedSkinWgt.set(geo.skinWeight, 0);
  }

  // Groups — preserve existing; append group for skin triangles
  const newGroups: GeometryGroup[] = geo.groups.map((g) => ({ ...g }));
  if (skin.indices.length > 0) {
    const maxMat = newGroups.reduce(
      (m, g) => Math.max(m, g.materialIndex ?? 0),
      -1,
    );
    newGroups.push({
      start: geo.indices.length,
      count: skin.indices.length,
      materialIndex: maxMat + 1,
    });
  }

  // Provenance: source verts pass through at identity [0..vertCount); every
  // generated tube/cap vert at [vertCount..) descends from the chain point
  // it encircles (`skin.sourceVerts` threads that mapping through).
  const outVertCount = vertCount + skinVertCount;
  const srcForNew = new Int32Array(outVertCount);
  for (let i = 0; i < vertCount; i++) srcForNew[i] = i;
  for (let i = 0; i < skinVertCount; i++) {
    srcForNew[vertCount + i] = skin.sourceVerts[i];
  }
  const provenance = provenanceFromSourceMap(vertCount, srcForNew);

  return {
    positions: combinedPos,
    indices: asSharedTriangleIndices(combinedIdx),
    normals: combinedNorm,
    uvs: combinedUVs,
    morphArrays: combinedMorphs,
    skinIndex: combinedSkinIdx,
    skinWeight: combinedSkinWgt,
    sculptDisplacement: combinedSculpt,
    groups: newGroups,
    morphTargetsRelative: geo.morphTargetsRelative,
    morphNames: geo.morphNames,
    // Wire edges and skin radii consumed — downstream modifiers see neither
    edges: null,
    edgeFlags: null,
    skinRadii: null,
    provenance,
  };
}
